import React, { useRef, useState } from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Heading } from '../../../_lib/components/Heading/Heading';
import { AcceptDeclineForm } from '../../../_lib/Form2/standardForms/AcceptDeclineForm/AcceptDeclineForm';
import { useDelayed } from '../../../hooks/useDelayed';
import type { Ticket as TicketType } from './Ticket';
import { Ticket } from './Ticket';
import { ModalInformation } from './TicketButton';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';
import { useRefreshView } from '../../../_lib/ViewFetcher';
import { SkipLink, SkipLinkTarget } from '../../../_lib/components/SkipLink';
import { Hint } from '../../../_lib/components/Hint';
import { Tile, TileGrid } from '../../../_lib/components/Tiles';
import { focusOnReturn } from '../../../lib/DomUtils/utils';

type TicketsProps = { tickets: TicketType[] };

export function Tickets({ tickets }: TicketsProps) {
  const { t } = useSonTranslation('TicketBooking');
  const skipLinkId = useHtmlValidId();
  const { refreshView } = useRefreshView();
  const [currentModal, setCurrentModal] = useState<ModalInformation>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const closeModal = () => {
    setCurrentModal(null);
  };
  const delayedCloseModal = useDelayed(closeModal);

  // avoiding empty lists
  if (tickets.length === 0) {
    return null;
  }

  return (
    <div className="ticket-booking">
      <Heading ref={headingRef} className="invisible ticket-booking__focusable-heading" hierarchy={4} tabIndex={-1}>
        {t('default.ticketsLabel')}
      </Heading>
      <SkipLink id={`skip-tickets-${skipLinkId}`}>{t('default.skipLinkText')}</SkipLink>
      <TileGrid>
        {tickets.map((ticket) => {
          return (
            <Tile key={ticket.ticketId}>
              <Ticket ticket={{ ...ticket }} openModal={setCurrentModal} />
            </Tile>
          );
        })}
      </TileGrid>
      <SkipLinkTarget targetName={t('default.skipLinkTargetText')} id={`skip-tickets-${skipLinkId}`} />
      {currentModal
        ? (
            <ModalDialog
              closeModal={() => {
                refreshView();
                closeModal();
              }}
              headline={currentModal.headline}
              initialFocusIndex={1}
              focusOnReturn={focusOnReturn(currentModal.ticketRef.current?.parentElement, headingRef.current)}
            >
              <AcceptDeclineForm
                headline={currentModal.question}
                form={currentModal.action}
                messageSuccess={currentModal.messageSuccess}
                messageSending={currentModal.messageSending}
                messageFail={currentModal.messageFail}
                customErrorTypeMessages={
                  currentModal.ticketAlreadyBooked
                    ? { ticketAlreadyBooked: currentModal.ticketAlreadyBooked }
                    : undefined
                }
                onSuccess={() => {
                  refreshView();
                  delayedCloseModal();
                }}
              >
                {currentModal.hint ? <Hint message={currentModal.hint} /> : null}
              </AcceptDeclineForm>
            </ModalDialog>
          )
        : null}
    </div>
  );
}
